import React, { useEffect, useMemo, useState } from "react";
import api from "@api";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import { useDomain } from "@useDomain";
import BannerSlideshow from "../../Sponsors/SponsorSlideshow.jsx";
import "./SchedulePublic.css";

function SchedulePublic({ page }) {
  const { slug, domain, organization } = useDomain();
  const navigate = useNavigate();

  const [games, setGames] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [byes, setByes] = useState([]);
  const [weekOffs, setWeekOffs] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedLeague, setSelectedLeague] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [gameStatusFilter, setGameStatusFilter] = useState("all");

  const [scheduleBanners, setScheduleBanners] = useState([]);
  const [bannerSettings, setBannerSettings] = useState({
    schedule: { rotationSpeed: 5, animation: "fade" },
  });
  const [features, setFeatures] = useState([]);
  const [scheduleType, setScheduleType] = useState("team");
  const content = page?.content || page?.pageSettings?.content || {};

  const filters = content?.filters || {
    showDateFilter: true,
    showLeagueFilter: true,
    showTeamFilter: true,
    showGameStatusFilter: true,
  };

  const rawThemeMode =
    content?.themeMode ||
    organization?.theme_mode ||
    "light";

  const getPlayerName = (player) => {
    if (!player) return "Unknown Player";

    return (
      player.name ||
      player.full_name ||
      `${player.firstName || ""} ${player.lastName || ""}`.trim() ||
      `${player.first_name || ""} ${player.last_name || ""}`.trim() ||
      player.username ||
      "Unknown Player"
    );
  };

  const getMatchupText = (entry) => {
    if (entry.homePlayer || entry.awayPlayer) {
      const homeName =
        entry.match_format === "doubles"
          ? `${getPlayerName(entry.homePlayer)} / ${getPlayerName(
            entry.homePartnerPlayer
          )}`
          : getPlayerName(entry.homePlayer);

      const awayName =
        entry.match_format === "doubles"
          ? `${getPlayerName(entry.awayPlayer)} / ${getPlayerName(
            entry.awayPartnerPlayer
          )}`
          : getPlayerName(entry.awayPlayer);

      return `${homeName} vs ${awayName}`;
    }

    return `${entry.homeTeam?.name || "Unknown Team"} vs ${entry.awayTeam?.name || "Unknown Team"
      }`;
  };

  const getResultText = (entry) => {
    const isIndividual = entry.homePlayer || entry.awayPlayer;

    if (isIndividual) {
      const match = entry.racquetMatches?.[0];

      if (!match?.scores?.length) {
        return "—";
      }

      return match.scores
        .map((set) => `${set.home_score}-${set.away_score}`)
        .join(", ");
    }

    return `${entry.score_team1 ?? 0} - ${entry.score_team2 ?? 0}`;
  };















  const scheduleTheme =
    String(rawThemeMode).trim().toLowerCase() === "dark" ? "dark" : "light";
  const visibleHeaders = content?.visibleHeaders || {
    date: true,
    location: true,
    matchup: true,
    time: true,
    results: true,
  };

  const visibleFilterCount = [
    filters.showDateFilter,
    filters.showLeagueFilter,
    filters.showTeamFilter,
    filters.showGameStatusFilter,
  ].filter(Boolean).length;

  const leagueOrder = [
    "Fountain Valley",
    "Tustin Columbus",
    "Irvine Valley College",
  ];

  const dayOrder = [
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
    "Sunday",
  ];

  useEffect(() => {
    if (!slug && !domain) return;

    const loadData = async () => {
      try {
        setLoading(true);

        const params = slug ? { slug } : { domain };

        const [gamesRes, leaguesRes, byesRes, weekOffsRes] = await Promise.all([
          api.get("/api/gamesv2", { params }),
          api.get("/api/leaguesv2", { params }),
          api.get("/api/bye-dates/public", { params }),
          api.get("/api/week-offs/public", { params }),
        ]);

        setGames(gamesRes.data.games || []);
        setLeagues(leaguesRes.data.leagues || []);
        setByes(byesRes.data.byes || []);
        setWeekOffs(weekOffsRes.data || []);
      } catch (err) {
        console.error("Error loading schedule data:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [slug, domain]);

  useEffect(() => {
    if (!organization?.id) return;

    const loadBannerData = async () => {
      try {
        const [bannersRes, featureRes, settingsRes] = await Promise.all([
          api.get(`/api/organization-banner/organization/${organization.id}`),
          api.get(`/api/organization-features/${organization.id}`),
          api.get(
            `/api/organization-features/${organization.id}/banner-settings/schedule`
          ),
        ]);

        const banners = bannersRes.data.banners || [];
        setScheduleBanners(
          banners.filter((b) => b.placement === "schedule" && b.enabled)
        );

        setFeatures(featureRes.data.features || []);

        setBannerSettings((prev) => ({
          ...prev,
          schedule: settingsRes.data,
        }));
      } catch (err) {
        console.error("Error loading banners/features:", err);
      }
    };

    loadBannerData();
  }, [organization]);

  const allowScheduleBanner = features.some(
    (f) => f.feature_key === "banner_schedule" && f.enabled
  );

  const activeGames = useMemo(() => {
    return games.filter((game) => game.season_id);
  }, [games]);



  const uniqueLeagues = useMemo(() => {
    const activeLeagueIds = [
      ...new Set(activeGames.map((g) => g.leagueId).filter(Boolean)),
    ];

    return leagues
      .filter(
        (l) => activeLeagueIds.includes(l.id) && l.seasons_leagues?.length > 0
      )
      .map((l) => ({
        id: l.id,
        name: l.name,
        league_type: l.league_type,
        seasons_leagues_id: l.seasons_leagues[0].id,
      }))
      .sort((a, b) => {
        const locationIndexA = leagueOrder.findIndex((loc) =>
          a.name.includes(loc)
        );
        const locationIndexB = leagueOrder.findIndex((loc) =>
          b.name.includes(loc)
        );

        if (locationIndexA !== locationIndexB) {
          if (locationIndexA === -1) return 1;
          if (locationIndexB === -1) return -1;
          return locationIndexA - locationIndexB;
        }

        const dayA = dayOrder.find((day) => a.name.includes(day)) || "";
        const dayB = dayOrder.find((day) => b.name.includes(day)) || "";
        const dayIndexA = dayOrder.indexOf(dayA);
        const dayIndexB = dayOrder.indexOf(dayB);

        if (dayIndexA === -1 && dayIndexB === -1) {
          return a.name.localeCompare(b.name);
        }
        if (dayIndexA === -1) return 1;
        if (dayIndexB === -1) return -1;
        return dayIndexA - dayIndexB;
      });
  }, [activeGames, leagues]);

  const hasTeamLeagues = uniqueLeagues.some(
    (league) => league.league_type === "team"
  );

  const hasIndividualLeagues = uniqueLeagues.some(
    (league) => league.league_type === "individual"
  );

  const hasMixedLeagueTypes =
    hasTeamLeagues && hasIndividualLeagues;

  const visibleLeagues = useMemo(() => {
    if (!hasMixedLeagueTypes) {
      return uniqueLeagues;
    }
    return uniqueLeagues.filter(
      (league) => league.league_type === scheduleType
    );
  }, [uniqueLeagues, hasMixedLeagueTypes, scheduleType]);

  const scheduleTypeGames = useMemo(() => {
    if (!hasMixedLeagueTypes) {
      return activeGames;
    }

    const leagueIds = visibleLeagues.map((league) => league.id);

    return activeGames.filter((game) =>
      leagueIds.includes(game.leagueId)
    );
  }, [activeGames, visibleLeagues, hasMixedLeagueTypes]);

  const uniqueDates = useMemo(() => {
    return [
      ...new Set(
        scheduleTypeGames
          .map((game) => game.date?.slice(0, 10))
          .filter(Boolean)
      ),
    ].sort();
  }, [scheduleTypeGames]);

  const selectedLeagueData = uniqueLeagues.find(
    (league) => league.id === Number(selectedLeague)
  );

  const selectedLeagueType =
    selectedLeagueData?.league_type || "";



  const uniqueTeams = useMemo(() => {
    const allGameTeams = activeGames.flatMap((game) =>
      [game.homeTeam, game.awayTeam].filter(Boolean)
    );

    const allTeams = [
      ...allGameTeams,
      ...byes.flatMap((bye) => bye.teams || []),
    ];

    return [...new Map(allTeams.map((team) => [team.id, team])).values()].sort(
      (a, b) => a.name.localeCompare(b.name)
    );
  }, [activeGames, byes]);

  const uniquePlayers = useMemo(() => {
    const allPlayers = scheduleTypeGames.flatMap((game) =>
      [game.homePlayer, game.awayPlayer].filter(Boolean)
    );

    return [
      ...new Map(allPlayers.map((player) => [player.id, player])).values(),
    ].sort((a, b) =>
      getPlayerName(a).localeCompare(getPlayerName(b))
    );
  }, [scheduleTypeGames]);


  let gamesForFilters = activeGames;

  if (hasMixedLeagueTypes) {
    const leagueIdsForType = uniqueLeagues
      .filter((league) => league.league_type === scheduleType)
      .map((league) => league.id);

    gamesForFilters = gamesForFilters.filter((game) =>
      leagueIdsForType.includes(game.leagueId)
    );
  }

  if (selectedDate) {
    gamesForFilters = gamesForFilters.filter(
      (game) => game.date?.slice(0, 10) === selectedDate
    );
  }

  if (selectedLeague) {
    gamesForFilters = gamesForFilters.filter(
      (game) => game.leagueId === Number(selectedLeague)
    );
  }

  if (selectedTeam) {
    gamesForFilters = gamesForFilters.filter(
      (game) =>
        game.homeTeam?.id === Number(selectedTeam) ||
        game.awayTeam?.id === Number(selectedTeam)
    );
  }
  if (selectedPlayer) {
    gamesForFilters = gamesForFilters.filter(
      (game) =>
        game.homePlayer?.id === Number(selectedPlayer) ||
        game.awayPlayer?.id === Number(selectedPlayer)
    );
  }
  let filteredGames = gamesForFilters;

  if (gameStatusFilter === "completed") {
    filteredGames = filteredGames.filter(
      (game) => Number(game.score_team1) > 0 || Number(game.score_team2) > 0
    );
  } else if (gameStatusFilter === "scheduled") {
    filteredGames = filteredGames.filter(
      (game) => !game.score_team1 && !game.score_team2
    );
  }

  const selectedSeasonsLeaguesId = uniqueLeagues.find(
    (l) => l.id === Number(selectedLeague)
  )?.seasons_leagues_id;

  const filteredByes = byes.filter((bye) => {
    const matchesDate =
      !selectedDate || bye.date?.slice(0, 10) === selectedDate;
    const matchesTeam =
      !selectedTeam ||
      bye.teams?.some((t) => t.id === Number(selectedTeam));
    const matchesLeague =
      !selectedLeague || bye.league_id === selectedSeasonsLeaguesId;

    return matchesDate && matchesTeam && matchesLeague;
  });

  const filteredWeekOffs = weekOffs.filter((off) => {
    const matchesDate =
      !selectedDate || off.date?.slice(0, 10) === selectedDate;
    const matchesLeague =
      !selectedLeague || off.seasons_leagues_id === selectedSeasonsLeaguesId;

    return matchesDate && matchesLeague;
  });

  const weekOffEntries = filteredWeekOffs.map((off, index) => ({
    id: `weekoff-${index}`,
    date: off.date,
    reason: off.reason,
    type: "week_off",
  }));

  const byeEntries = filteredByes.map((bye, index) => ({
    id: `bye-${index}`,
    date: bye.date,
    teams: (bye.teams || []).map((t) => ({ id: t.id, name: t.name })),
    type: "bye",
  }));

  const displayRows = [...filteredGames, ...byeEntries, ...weekOffEntries].sort(
    (a, b) => {
      const dateA = new Date(`${a.date}T${a.time || "00:00"}`);
      const dateB = new Date(`${b.date}T${b.time || "00:00"}`);
      return dateA - dateB;
    }
  );

  const visibleColumnCount = [
    visibleHeaders.date,
    visibleHeaders.location,
    visibleHeaders.matchup,
    visibleHeaders.time,
    visibleHeaders.results,
  ].filter(Boolean).length;

  if (loading) return null;

  return (
    <div className={`schedulePublic-container schedule-theme-${scheduleTheme}`}>
      {allowScheduleBanner && scheduleBanners.length > 0 && (
        <BannerSlideshow
          banners={scheduleBanners}
          rotationSpeed={bannerSettings.schedule.rotationSpeed}
          animation={bannerSettings.schedule.animation}
        />
      )}

      {visibleFilterCount > 0 && (
        <div className="schedulePublic-league-name-container">
          <div className="schedulePublic-league-title">
            <div className="schedulePublic-filter-content">

              {hasMixedLeagueTypes && (
                <div className="schedulePublic-type-filter">
                  <button
                    type="button"
                    className={scheduleType === "team" ? "active" : ""}
                    onClick={() => {
                      setScheduleType("team");
                      setSelectedLeague("");
                      setSelectedTeam("");
                    }}
                  >
                    Team
                  </button>

                  <button
                    type="button"
                    className={scheduleType === "individual" ? "active" : ""}
                    onClick={() => {
                      setScheduleType("individual");
                      setSelectedLeague("");
                      setSelectedTeam("");
                    }}
                  >
                    Individual
                  </button>
                </div>
              )}

              <div
                className={`date-filter-wrapper filter-count-${visibleFilterCount}`}
              >
                {filters.showDateFilter && (
                <div className="schedule-filter-item">
                  <select
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                  >
                    <option value="">All Dates</option>
                    {uniqueDates.map((date) => (
                      <option key={date} value={date}>
                        {formatInTimeZone(
                          `${date}T12:00:00`,
                          "America/Los_Angeles",
                          "MM/dd/yy"
                        )}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {filters.showLeagueFilter && (
                <div className="schedule-filter-item">
                  <select
                    value={selectedLeague}
                    onChange={(e) => setSelectedLeague(e.target.value)}
                  >
                    <option value="">All Leagues</option>
                      {visibleLeagues.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                  </select>
                </div>
              )}
              {filters.showTeamFilter &&
                (!hasMixedLeagueTypes || scheduleType === "team") && (
                <div className="schedule-filter-item">
                  <select
                    value={selectedTeam}
                    onChange={(e) => setSelectedTeam(e.target.value)}
                  >
                    <option value="">All Teams</option>
                    {uniqueTeams.map((team) => (
                      <option key={team.id} value={team.id}>
                        {team.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
                {hasMixedLeagueTypes && scheduleType === "individual" && (
                  <div className="schedule-filter-item">
                    <select
                      value={selectedPlayer}
                      onChange={(e) => setSelectedPlayer(e.target.value)}
                    >
                      <option value="">All Players</option>

                      {uniquePlayers.map((player) => (
                        <option key={player.id} value={player.id}>
                          {getPlayerName(player)}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              {filters.showGameStatusFilter && (
                <div className="schedule-filter-item">
                  <select
                    value={gameStatusFilter}
                    onChange={(e) => setGameStatusFilter(e.target.value)}
                  >
                    <option value="all">All Games</option>
                    <option value="scheduled">Scheduled Games</option>
                    <option value="completed">Completed Games</option>
                  </select>
                </div>
                 
              )}
            </div>
          </div>
          </div>
        </div>  
      )}

      {displayRows.length > 0 ? (
        <div className="schedulePublic-game-container">
          <table className="schedulePublic-game-container-table">
            <thead>
              <tr className="schedule-head-font">
                {visibleHeaders.date && <th>Date</th>}
                {visibleHeaders.location && <th>Location</th>}
                {visibleHeaders.matchup && <th>Matchup</th>}
                {visibleHeaders.time && <th>Time</th>}
                {visibleHeaders.results && <th>Results</th>}
              </tr>
            </thead>

            <tbody>
              {displayRows.map((entry) => {
                if (entry.type === "week_off") {
                  return (
                    <tr key={entry.id} className="week-off-row">
                      {visibleHeaders.date && (
                        <td>
                          {formatInTimeZone(
                            new Date(`${entry.date}T12:00:00`),
                            "America/Los_Angeles",
                            "MM/dd/yy"
                          )}
                        </td>
                      )}

                      {visibleColumnCount > (visibleHeaders.date ? 1 : 0) && (
                        <td
                          colSpan={
                            visibleColumnCount - (visibleHeaders.date ? 1 : 0)
                          }
                        >
                          🛑 No Games ({entry.reason || "Off Week"})
                        </td>
                      )}
                    </tr>
                  );
                }

                if (entry.type === "bye") {
                  return (
                    <tr key={entry.id} className="bye-row">
                      {visibleHeaders.date && (
                        <td>
                          {formatInTimeZone(
                            new Date(`${entry.date}T12:00:00`),
                            "America/Los_Angeles",
                            "MM/dd/yy"
                          )}
                        </td>
                      )}

                      {visibleHeaders.location && <td></td>}
                      {visibleHeaders.matchup && (
                        <td>{entry.teams.map((t) => t.name).join(", ")} (BYE)</td>
                      )}
                      {visibleHeaders.time && <td>BYE</td>}
                      {visibleHeaders.results && <td>BYE</td>}
                    </tr>
                  );
                }

                // const homeTeam = entry.homeTeam;
                // const awayTeam = entry.awayTeam;
                // const homeScore = entry.score_team1;
                // const awayScore = entry.score_team2;

                return (
                  <tr
                    key={entry.id}
                    onClick={() =>
                      navigate(
                        slug ? `/${slug}/games/${entry.id}` : `/games/${entry.id}`
                      )
                    }
                    style={{ cursor: "pointer" }}
                    className="clickable-row"
                  >
                    {visibleHeaders.date && (
                      <td>
                        {entry.date
                          ? formatInTimeZone(
                            new Date(`${entry.date}T12:00:00`),
                            "America/Los_Angeles",
                            "MM/dd/yy"
                          )
                          : "TBD"}
                      </td>
                    )}

                    {visibleHeaders.location && (
                      <td>{entry.location ? entry.location : ""}</td>
                    )}

                    {visibleHeaders.matchup && (
                      <td>{getMatchupText(entry)}</td>
                    )}

                    {visibleHeaders.time && (
                      <td>
                        {entry.time && /^\d{2}:\d{2}$/.test(entry.time)
                          ? format(new Date(`${entry.date}T${entry.time}`), "h:mm a")
                          : "TBD"}
                      </td>
                    )}

                    {visibleHeaders.results && (
                      <td>{getResultText(entry)}</td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <p>No games, byes, or week-offs found.</p>
      )}
    </div>
  );
}

export default SchedulePublic;